import { useState, type FormEvent } from "react";
import { Check, Copy, KeyRound, Mail, MessageSquare, RotateCcw, Share2 } from "lucide-react";
import type { CollectPage } from "@guestbook/shared";
import { Button } from "@/shared/components/ui/Button";
import { Input } from "@/shared/components/ui/Input";
import { Switch } from "@/shared/components/ui/Switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/components/ui/AlertDialog";
import { FormField } from "@/shared/components/form/FormField";
import { useMe } from "@/shared/lib/auth";
import {
  buildShareTargets,
  canNativeShare,
  nativeShare,
  shareUrl,
  useCollectPage,
  useResetCollectPageLink,
  useUpdateCollectPage,
} from "@/shared/lib/collectPage";

/**
 * The "share" half of the Collect Contacts page. The account has one persistent
 * page the owner publishes/unpublishes, shares (copy / social targets), gates
 * with an optional access code, and can reset the link for.
 */
export function CollectShareSection() {
  const page = useCollectPage();

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="type-heading-sm text-text">Your collect page</h2>
        <p className="type-body-sm text-text-muted">
          Publish your page, then share its link by text, email, or social. Whatever people
          submit waits here for your review before it's added.
        </p>
      </div>

      {page.isLoading ? (
        <p className="type-body-base text-text-muted">Loading…</p>
      ) : page.isError ? (
        <p className="type-body-sm text-danger-strong" role="alert">
          Couldn't load your collect page: {page.error.message}
        </p>
      ) : page.data ? (
        <CollectPagePanel page={page.data} />
      ) : null}
    </section>
  );
}

function CollectPagePanel({ page }: { page: CollectPage }) {
  const me = useMe();
  const update = useUpdateCollectPage();
  const url = shareUrl(page.token);
  const [copied, setCopied] = useState(false);

  const shareOpts = { requesterName: me.data?.name ?? null, accessCode: page.accessCode };
  const targets = buildShareTargets(url, shareOpts);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be unavailable (insecure context / denied). The input is
      // selectable as a manual fallback.
    }
  }

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-border-muted glass-surface-subtle p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-0.5">
          <span className="type-label-base tracking-wider uppercase text-text">
            {page.published ? "Page is live" : "Page is off"}
          </span>
          <span className="type-body-sm text-text-muted">
            {page.published
              ? "Anyone with the link can open your page and submit their details."
              : "Your page is paused — visitors see a notice that sharing is turned off."}
          </span>
        </div>
        <Switch
          checked={page.published}
          onCheckedChange={(next) => update.mutate({ published: next })}
          disabled={update.isPending}
          aria-label="Publish collect page"
        />
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input
          readOnly
          value={url}
          aria-label="Share link URL"
          className="flex-1"
          onFocus={(e) => e.currentTarget.select()}
        />
        <div className="flex shrink-0 gap-2">
          <Button variant="secondary" size="sm" onClick={copy}>
            {copied ? (
              <Check aria-hidden className="h-4 w-4" />
            ) : (
              <Copy aria-hidden className="h-4 w-4" />
            )}
            {copied ? "Copied" : "Copy"}
          </Button>
          {canNativeShare() ? (
            <Button variant="secondary" size="sm" onClick={() => nativeShare(url, shareOpts)}>
              <Share2 aria-hidden className="h-4 w-4" />
              Share
            </Button>
          ) : null}
        </div>
      </div>

      {page.published ? (
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="secondary" size="sm">
            <a href={targets.sms}>
              <MessageSquare aria-hidden className="h-4 w-4" />
              Text
            </a>
          </Button>
          <Button asChild variant="secondary" size="sm">
            <a href={targets.email}>
              <Mail aria-hidden className="h-4 w-4" />
              Email
            </a>
          </Button>
          <Button asChild variant="secondary" size="sm">
            <a href={targets.whatsapp} target="_blank" rel="noreferrer">
              WhatsApp
            </a>
          </Button>
          <Button asChild variant="secondary" size="sm">
            <a href={targets.messenger}>Messenger</a>
          </Button>
        </div>
      ) : (
        <p className="type-body-sm text-text-muted">
          Turn the page on to share it. You can still copy the link and set an access code
          while it's off.
        </p>
      )}

      <AccessCodePanel page={page} />

      <div className="flex items-center justify-between gap-2">
        <span className="type-body-sm text-text-muted" role="status">
          {copied ? "Link copied to clipboard" : ""}
        </span>
        <ResetLinkButton />
      </div>
    </div>
  );
}

function AccessCodePanel({ page }: { page: CollectPage }) {
  const update = useUpdateCollectPage();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(page.accessCode ?? "");
  const [copied, setCopied] = useState(false);

  function startEdit() {
    setValue(page.accessCode ?? "");
    setEditing(true);
  }

  function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    update.mutate(
      { accessCode: value.trim() },
      { onSuccess: () => setEditing(false) },
    );
  }

  function remove() {
    update.mutate({ accessCode: null });
  }

  async function copyCode() {
    if (!page.accessCode) return;
    try {
      await navigator.clipboard.writeText(page.accessCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }

  if (editing) {
    return (
      <form className="flex flex-col gap-2 rounded-lg bg-surface-muted/40 p-3" onSubmit={save}>
        <FormField
          label="Access code"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          hint="Recipients must enter this to open the page. It's included in the shared message."
          error={update.isError ? update.error.message : undefined}
          autoFocus
        />
        <div className="flex gap-2">
          <Button type="submit" size="sm" disabled={update.isPending || !value.trim()}>
            {update.isPending ? "Saving…" : "Save code"}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg bg-surface-muted/40 p-3">
      <KeyRound aria-hidden className="h-4 w-4 text-text-muted" />
      {page.accessCode ? (
        <>
          <span className="type-body-sm text-text">
            Access code: <span className="font-medium">{page.accessCode}</span>
          </span>
          <Button variant="ghost" size="sm" onClick={copyCode}>
            {copied ? "Copied" : "Copy"}
          </Button>
          <Button variant="ghost" size="sm" onClick={startEdit}>
            Change
          </Button>
          <Button
            variant="ghost-danger"
            size="sm"
            onClick={remove}
            disabled={update.isPending}
          >
            Remove
          </Button>
        </>
      ) : (
        <>
          <span className="type-body-sm text-text-muted">
            No access code — anyone with the link can submit.
          </span>
          <Button variant="ghost" size="sm" onClick={startEdit}>
            Add access code
          </Button>
        </>
      )}
    </div>
  );
}

function ResetLinkButton() {
  const reset = useResetCollectPageLink();
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost-danger" size="sm">
          <RotateCcw aria-hidden className="h-4 w-4" />
          Reset link
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reset this link?</AlertDialogTitle>
          <AlertDialogDescription>
            A new link will be generated and the current one will stop working immediately.
            Anyone you've already shared the old link with will need the new one. Submissions
            you've already received are kept.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction asChild>
            <Button
              variant="destructive"
              onClick={() => reset.mutate()}
              disabled={reset.isPending}
            >
              {reset.isPending ? "Resetting…" : "Reset link"}
            </Button>
          </AlertDialogAction>
          <AlertDialogCancel asChild>
            <Button variant="ghost">Cancel</Button>
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
